import { BarChart3, LogOut, Megaphone, Moon, MoreHorizontal, Plus, Recycle, Search, Settings, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { GuideButton } from "@/components/guide/GuideButton";
import { GuideProvider } from "@/components/guide/GuideContext";
import "@/guide.css";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { NotificationBell } from "@/components/NotificationBell";
import { homeFor } from "@/permissions";
import { setIncomingHandler, stopPhone } from "@/lib/ringoverPhone";
import { toast } from "sonner";
import { driftMesh, magnetic, slidePill, squish } from "@/themes/rapport/motion";
import { useThemeMode } from "@/themes";
import "@/inbox.css";
import "@/projects.css";

const MARKETING = [
  { to: "/demandes", label: "Demandes du site" },
  { to: "/avis-google", label: "Avis Google" },
  { to: "/devis/validation", label: "Validation devis" },
  { to: "/parcours", label: "Parcours de relance" },
  { to: "/envois", label: "File d’envoi", queueBadge: true },
];
const SETTINGS = [
  { to: "/utilisateurs", label: "Utilisateurs" },
  { to: "/parametres", label: "Connexion noCRM" },
  { to: "/transfert-nocrm", label: "Transfert de la base noCRM" },
  { to: "/parametres-pipeline", label: "Pipelines" },
  { to: "/ringover", label: "Ringover" },
  { to: "/devis", label: "Paramètres devis" },
  { to: "/gmail", label: "Gmail" },
  { to: "/activite", label: "Fonctions classées · Activité" },
];

const tabs = [
  { to: "/accueil", label: "Accueil", member: true },
  { to: "/reponses", label: "Réponses", badge: true, module: "reponses" },
  { to: "/pipeline", label: "Pipeline", module: "pipeline" },
  { to: "/cockpit", label: "Cockpit", module: "pipeline" },
  { to: "/clients", label: "Clients", module: "pipeline" },
  { to: "/projets", label: "Projets", module: "projets", taskBadge: true },
  { to: "/statistiques", label: "Statistiques", module: "statistiques", also: ["/statistiques-agents"] },
];

// Sous-menu des rubriques qui ont plusieurs pages (affiché sous l'en-tête)
const SUBNAV = { statistiques: [{ to: "/statistiques", label: "Rapports d'activité" }, { to: "/statistiques-agents", label: "Production des agents" }] };

// Menu « Plus » du dock : toutes les pages secondaires, rangées par rubrique
const MORE = [
  { label: "Automatisation", module: "marketing", children: [{ to: "/automatisation-etapes", label: "D'une étape à l'autre" }, { to: "/automatisations", label: "Emails automatiques" }] },
  { label: "Fichiers de prospection", module: "prospection", children: [{ to: "/preparer-fichier", label: "Préparer un fichier" }, { to: "/recyclage", label: "Recycler la base" }, { to: "/recyclage-leads", label: "Recyclage de leads" }, { to: "/fichiers-lobstr", label: "Fichiers en cours" }] },
  { label: "Statistiques", module: "statistiques", children: [{ to: "/statistiques", label: "Rapports d'activité" }, { to: "/statistiques-agents", label: "Production des agents" }] },
  { label: "Marketing", module: "marketing", children: MARKETING },
  { label: "Paramètres", admin: true, children: SETTINGS },
];

const dock = [
  { to: "/pipeline?focus=search", label: "Rechercher une opportunité", icon: Search, module: "pipeline" },
  { to: "/pipeline?new=1", label: "Nouvelle opportunité", icon: Plus, module: "pipeline" },
  { to: "/statistiques-agents", label: "Production des agents", icon: BarChart3, module: "statistiques" },
  { to: "/recyclage-leads", label: "Recyclage de leads", icon: Recycle, module: "prospection", phoneHide: true },
  { to: "/envois", label: "Campagnes · file d’envoi", icon: Megaphone, module: "marketing", phoneHide: true },
];

const slug = (label) => label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

function useIncomingCalls() {
  const goTo = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    let last = "";
    setIncomingHandler(async (number) => {
      if (!number || number === last) return;
      last = number;
      setTimeout(() => { last = ""; }, 15000);
      try {
        const { data } = await api.get("/phone/lookup", { params: { number } });
        if (data.lead) { toast.info(`Appel entrant : ${data.lead.title}`); goTo(`/pipeline?lead=${data.lead.lead_id}&t=${Date.now()}`); }
        else if (data.contact) { toast.info(`Appel entrant : ${data.contact.name || number}`); goTo(`/fichiers-lobstr?file=${data.contact.file_id}&contact=${data.contact.id}&t=${Date.now()}`); }
        else toast.info(`Appel entrant du ${number} : aucune fiche trouvée.`);
      } catch { /* recherche indisponible */ }
    });
    return () => setIncomingHandler(null);
  }, [goTo]);
  useEffect(() => () => stopPhone(), [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useCounts(guest, canInbox) {
  const [counts, setCounts] = useState({ inbox: 0, queue: 0, tasks: { late: 0, today: 0 } });
  useEffect(() => {
    if (guest) return undefined;
    const set = (k) => (v) => setCounts((c) => ({ ...c, [k]: v }));
    const tasks = () => api.get("/tasks/summary").then(({ data }) => set("tasks")(data)).catch(() => {});
    const load = () => {
      if (canInbox) api.get("/inbox/badge").then(({ data }) => set("inbox")(data.count)).catch(() => {});
      api.get("/send-queue", { params: { limit: 1 } }).then(({ data }) => set("queue")(data.pending)).catch(() => {});
      tasks();
    };
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener("tasks-changed", tasks);
    return () => { clearInterval(timer); window.removeEventListener("tasks-changed", tasks); };
  }, [guest, canInbox]);
  return counts;
}

// Menu déroulant refermé au clic extérieur, au changement de page, au défilement et au redimensionnement
function usePopMenu() {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setPos(null), [pathname]);
  useEffect(() => {
    if (!pos) return undefined;
    const outside = (e) => { if (!ref.current?.contains(e.target)) setPos(null); };
    const close = () => setPos(null);
    document.addEventListener("mousedown", outside);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", outside, true);
    return () => { document.removeEventListener("mousedown", outside); window.removeEventListener("resize", close); window.removeEventListener("scroll", outside, true); };
  }, [pos]);
  const toggle = (e, alignRight) => {
    if (pos) { setPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const width = 250;
    const left = alignRight ? Math.max(8, r.right - width) : Math.min(r.left, window.innerWidth - width - 8);
    setPos({ top: r.bottom + 6, left: Math.max(8, left) });
  };
  return { ref, open: !!pos, style: pos ? { position: "fixed", top: pos.top, left: pos.left, right: "auto" } : undefined, toggle };
}

function DockMore({ sections, queueCount }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setPos(null), [pathname]);
  useEffect(() => {
    if (!pos) return undefined;
    const outside = (e) => { if (!ref.current?.contains(e.target)) setPos(null); };
    const close = () => setPos(null);
    document.addEventListener("mousedown", outside);
    window.addEventListener("resize", close);
    return () => { document.removeEventListener("mousedown", outside); window.removeEventListener("resize", close); };
  }, [pos]);
  const toggle = (e) => {
    squish(e.currentTarget);
    if (pos) { setPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const h = Math.min(520, window.innerHeight * 0.7);
    const side = r.right + 280 < window.innerWidth;
    setPos(side
      ? { left: r.right + 14, top: Math.max(8, Math.min(r.top - h / 2, window.innerHeight - h - 8)) }
      : { left: Math.max(8, window.innerWidth - 288), top: Math.max(8, r.top - h - 12) });
  };
  const has = sections.some((s) => s.children.some((c) => c.to === pathname));
  return (
    <div ref={ref}>
      <button type="button" className={`dock__btn dock__plus magnetic${has ? " is-on" : ""}`} onClick={toggle} aria-haspopup="menu" aria-expanded={!!pos} aria-label="Plus de pages" data-testid="dock-more">
        <MoreHorizontal size={18} /><span className="dock__tip">Plus</span>
      </button>
      {pos && (
        <div className="pop crm-more" role="menu" style={pos} data-testid="dock-more-menu">
          {sections.map((s, i) => (
            <div key={s.label} className="crm-more__sec">
              {i > 0 && <hr />}
              <small>{s.label}</small>
              {s.children.map((c) => (
                <NavLink key={c.to} to={c.to} end className="menu__item" role="menuitem" data-testid={`navigation-${slug(c.label)}`}>
                  <span>{c.label}</span>
                  {c.queueBadge && queueCount > 0 && <span className="nav-badge queue-badge menu__check" data-testid="queue-nav-badge">{queueCount}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, guest, logout }) {
  const { ref, open, style, toggle } = usePopMenu();
  return (
    <div className="ds-user" ref={ref} data-testid="sidebar-user">
      <button type="button" className="ds-avatar" onClick={(e) => toggle(e, true)} title={user?.name} data-testid="user-menu-btn">{initials(user?.name)}</button>
      {open && (
        <div className="ds-menu ds-menu-right" style={style} data-testid="user-menu">
          <div className="ds-user-info"><strong>{user?.name}</strong><small>{user?.role === "admin" ? "Administrateur" : guest ? "Invité externe" : "Utilisateur"}</small></div>
          <button type="button" className="ds-menu-action" onClick={logout} data-testid="logout-btn"><LogOut size={14} /> Se déconnecter</button>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children }) {
  useIncomingCalls();
  const { dark, toggleTheme } = useThemeMode();
  useEffect(() => () => { document.documentElement.dataset.theme = "light"; }, []);
  const goTo = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const guest = user?.role === "guest";
  const isAdmin = user?.role === "admin";
  const allowed = (item) => (item.admin ? isAdmin : item.member ? !guest : !item.module || isAdmin || user?.modules?.includes(item.module));
  const canInbox = isAdmin || !!user?.modules?.includes("reponses");
  const { inbox, queue, tasks } = useCounts(guest, canInbox);
  const visibleTabs = tabs.filter(allowed);
  const moreSections = MORE.filter(allowed);

  const track = useRef(null);
  const pill = useRef(null);
  useEffect(() => driftMesh(), []);
  useEffect(() => {
    const t = track.current;
    if (!t) return undefined;
    const move = (animate) => slidePill(pill.current, t, t.querySelector(".tab.active"), animate);
    move(true);
    const ro = new ResizeObserver(() => move(false));
    ro.observe(t);
    document.fonts?.ready.then(() => move(false));
    return () => ro.disconnect();
  }, [location.pathname, visibleTabs.length]);
  useEffect(() => { magnetic(document.querySelector(".dock")); }, []);

  return (
    <GuideProvider>
      <div className="mesh" aria-hidden="true"><i className="mesh__a" /><i className="mesh__b" /><i className="mesh__c" /></div>
      <div className="app app-shell" data-testid="application-shell">
        <header className="top">
          <NavLink to={homeFor(user)} end className="brand" data-testid="application-brand">
            <img src="/airthermo-logo-site.png" alt="Airthermo — Cabinet de courtage énergie" className="brand__logo brand__logo--light" data-testid="application-logo" />
            <img src="/airthermo-logo-site-dark.png" alt="" aria-hidden="true" className="brand__logo brand__logo--dark" />
          </NavLink>
          <nav className="tabs" aria-label="Rubriques" data-testid="main-navigation">
            <div className="tabs__track" ref={track}>
              {visibleTabs.map((t) => (
                <NavLink key={t.to} to={t.to} className={({ isActive }) => `tab${isActive || t.also?.includes(location.pathname) ? " active" : ""}`} data-testid={`navigation-${slug(t.label)}`}>
                  {t.label}
                  {t.badge && inbox > 0 && <span className="nav-badge" data-testid="inbox-nav-badge">{inbox}</span>}
                  {t.taskBadge && tasks.late + tasks.today > 0 && <span className={`nav-badge ${tasks.late ? "" : "task-badge-today"}`} title={`${tasks.late} en retard · ${tasks.today} aujourd'hui`} data-testid="tasks-nav-badge">{tasks.late + tasks.today}</span>}
                </NavLink>
              ))}
              <div className="tabs__pill crm-pill" ref={pill} aria-hidden="true" />
            </div>
          </nav>
          <div className="top__tools ds-top-actions">
            <GuideButton />
            <NotificationBell />
            <UserMenu user={user} guest={guest} logout={logout} />
          </div>
        </header>
        {!guest && (
          <nav className="dock" aria-label="Raccourcis" data-testid="app-dock">
            {dock.filter(allowed).map((d) => (d.to.includes("?")
              ? <button key={d.to} type="button" className="dock__btn magnetic" onClick={(e) => { squish(e.currentTarget); goTo(d.to); }} aria-label={d.label} data-testid={`dock-${slug(d.label)}`}><d.icon size={18} /><span className="dock__tip">{d.label}</span></button>
              : <NavLink key={d.to} to={d.to} className={`dock__btn magnetic${d.phoneHide ? " dock__opt" : ""}`} onClick={(e) => squish(e.currentTarget)} aria-label={d.label} data-testid={`dock-${slug(d.label)}`}><d.icon size={18} /><span className="dock__tip">{d.label}</span></NavLink>))}
            {moreSections.length > 0 && <DockMore sections={moreSections} queueCount={queue} />}
            <span className="dock__sep" />
            {isAdmin && <NavLink to="/parametres" className="dock__btn magnetic" onClick={(e) => squish(e.currentTarget)} aria-label="Paramètres" data-testid="dock-parametres"><Settings size={18} /><span className="dock__tip">Paramètres</span></NavLink>}
            <button type="button" className="dock__btn dock__theme magnetic" onClick={(e) => { squish(e.currentTarget); toggleTheme(); }} aria-label={dark ? "Passer en mode clair" : "Passer en mode sombre"} data-testid="theme-toggle">
              {dark ? <Sun size={18} /> : <Moon size={18} />}<span className="dock__tip">{dark ? "Mode clair" : "Mode sombre"}</span>
            </button>
          </nav>
        )}
        {location.pathname.startsWith("/statistiques") && (
          <nav className="subnav" aria-label="Statistiques" data-testid="stats-subnav">
            {SUBNAV.statistiques.map((l) => <NavLink key={l.to} to={l.to} end className="subnav__link" data-testid={`stats-subnav-${slug(l.label)}`}>{l.label}</NavLink>)}
          </nav>
        )}
        <main className="main main-area" key={location.pathname}>{children}</main>
      </div>
    </GuideProvider>
  );
}
